/**
 * Recordings List Page (T125)
 * Admin page with filters: date range, processing status, customer access
 */

import { useState, useCallback } from 'react';
import {
  Video,
  Search,
  HardDrive,
  Users,
  Calendar,
  ChevronLeft,
  ChevronRight,
  FileText,
} from 'lucide-react';
import {
  useRecordings,
  useTranscriptSearch,
  formatDuration,
  formatFileSize,
  type Recording,
  type RecordingFilters,
} from '../hooks/useRecordings';
import RecordingDetail from '../components/Recordings/RecordingDetail';
import { RecordingsGridSkeleton } from '../components/ui/SkeletonLoaders';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'ARCHIVED', label: 'Archived' },
  { value: 'ERROR', label: 'Error' },
];

const STATUS_BADGE: Record<string, string> = {
  PROCESSING: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
  AVAILABLE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  ARCHIVED: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  UNAVAILABLE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  ERROR: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
};

export default function RecordingsPage() {
  const [filters, setFilters] = useState<RecordingFilters>({
    page: 1,
    limit: 12,
    sortBy: 'recordedAt',
    sortOrder: 'desc',
  });
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<'list' | 'transcript'>('list');
  const [transcriptQuery, setTranscriptQuery] = useState('');

  const { data, isLoading } = useRecordings(filters);
  const { data: searchResults, isLoading: isSearching } = useTranscriptSearch(
    searchMode === 'transcript' ? transcriptQuery : ''
  );

  const setFilter = useCallback(
    (key: keyof RecordingFilters, value: RecordingFilters[keyof RecordingFilters]) =>
      setFilters((prev) => ({ ...prev, [key]: value, page: 1 })),
    []
  );

  const recordings = data?.recordings || [];
  const pagination = data?.pagination || { page: 1, limit: 12, total: 0, totalPages: 0 };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Recordings</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Manage session recordings, transcripts, and customer access
          </p>
        </div>
        <div className="text-sm text-muted-foreground">
          {pagination.total} recording{pagination.total !== 1 ? 's' : ''}
        </div>
      </div>

      {/* Search + Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-3">
        {/* Search mode toggle */}
        <div className="flex bg-muted/40 rounded-lg p-0.5">
          <button
            onClick={() => setSearchMode('list')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              searchMode === 'list'
                ? 'bg-background shadow-sm font-medium'
                : 'text-muted-foreground'
            }`}
          >
            <Video className="w-4 h-4 inline mr-1" />
            List
          </button>
          <button
            onClick={() => setSearchMode('transcript')}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              searchMode === 'transcript'
                ? 'bg-background shadow-sm font-medium'
                : 'text-muted-foreground'
            }`}
          >
            <Search className="w-4 h-4 inline mr-1" />
            Transcript Search
          </button>
        </div>

        {searchMode === 'transcript' ? (
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search transcript content..."
              value={transcriptQuery}
              onChange={(e) => setTranscriptQuery(e.target.value)}
              className="claude-input pl-10 w-full text-sm"
            />
          </div>
        ) : (
          <>
            {/* Status filter */}
            <select
              value={filters.processingStatus || ''}
              onChange={(e) => setFilter('processingStatus', e.target.value || undefined)}
              className="claude-input text-sm"
            >
              {STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            {/* Date range */}
            <input
              type="date"
              value={filters.startDate?.split('T')[0] || ''}
              onChange={(e) =>
                setFilter(
                  'startDate',
                  e.target.value ? `${e.target.value}T00:00:00.000Z` : undefined
                )
              }
              className="claude-input text-sm"
              placeholder="From date"
            />
            <input
              type="date"
              value={filters.endDate?.split('T')[0] || ''}
              onChange={(e) =>
                setFilter('endDate', e.target.value ? `${e.target.value}T23:59:59.999Z` : undefined)
              }
              className="claude-input text-sm"
              placeholder="To date"
            />

            {/* Customer access toggle */}
            <button
              onClick={() =>
                setFilter('hasAccess', filters.hasAccess === undefined ? true : undefined)
              }
              className={`claude-button text-sm ${
                filters.hasAccess ? 'claude-button-primary' : 'claude-button-secondary'
              }`}
            >
              <Users className="w-4 h-4 inline mr-1" />
              {filters.hasAccess ? 'Shared Only' : 'All Access'}
            </button>
          </>
        )}
      </div>

      {/* Transcript Search Results */}
      {searchMode === 'transcript' && transcriptQuery.length >= 2 && (
        <div className="space-y-2">
          {isSearching ? (
            <div className="flex items-center justify-center py-12">
              <div className="claude-spinner" />
            </div>
          ) : searchResults?.results && searchResults.results.length > 0 ? (
            <>
              <p className="text-sm text-muted-foreground">
                {searchResults.pagination.total} result
                {searchResults.pagination.total !== 1 ? 's' : ''}
              </p>
              {searchResults.results.map((result) => (
                <button
                  key={`${result.recordingId}-${result.snippet.slice(0, 20)}`}
                  onClick={() => setSelectedId(result.recordingId)}
                  className="w-full text-left claude-card p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="font-medium text-sm">{result.appointmentTitle}</p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {result.customerName} •{' '}
                        {new Date(result.appointmentDate).toLocaleDateString()}
                      </p>
                    </div>
                    <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  </div>
                  <p className="text-sm mt-2 text-muted-foreground leading-relaxed">
                    &ldquo;{result.snippet}&rdquo;
                  </p>
                </button>
              ))}
            </>
          ) : (
            <p className="text-center text-sm text-muted-foreground py-8">
              No transcript matches found.
            </p>
          )}
        </div>
      )}

      {/* Recordings Grid */}
      {searchMode === 'list' && (
        <>
          {isLoading ? (
            <RecordingsGridSkeleton />
          ) : recordings.length === 0 ? (
            <div className="text-center py-16">
              <Video className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
              <h3 className="text-lg font-medium">No recordings found</h3>
              <p className="text-muted-foreground text-sm mt-1">
                Recordings will appear here after Zoom sessions are synced.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recordings.map((rec: Recording) => (
                <button
                  key={rec.id}
                  onClick={() => setSelectedId(rec.id)}
                  className="claude-card p-4 text-left hover:shadow-md transition-shadow"
                >
                  {/* Thumbnail / Placeholder */}
                  <div className="aspect-video bg-muted/30 rounded-lg mb-3 flex items-center justify-center relative overflow-hidden">
                    {rec.thumbnailUrl ? (
                      <img
                        src={rec.thumbnailUrl}
                        alt="Recording thumbnail"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Video className="w-10 h-10 text-muted-foreground/30" />
                    )}
                    {rec.duration && (
                      <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-1.5 py-0.5 rounded">
                        {formatDuration(rec.duration)}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <h3 className="font-medium text-sm truncate">
                    {rec.appointmentTitle || 'Untitled'}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {rec.customerName || 'Unknown'}
                  </p>

                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {rec.recordedAt && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(rec.recordedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                          })}
                        </span>
                      )}
                      {rec.fileSize && (
                        <span className="flex items-center gap-1">
                          <HardDrive className="w-3 h-3" />
                          {formatFileSize(rec.fileSize)}
                        </span>
                      )}
                    </div>
                    <span
                      className={`text-xs px-1.5 py-0.5 rounded-full ${STATUS_BADGE[rec.processingStatus] || ''}`}
                    >
                      {rec.processingStatus}
                    </span>
                  </div>

                  {/* Access indicator */}
                  {(rec.accessGrantCount ?? 0) > 0 && (
                    <div className="flex items-center gap-1 mt-2 text-xs text-primary">
                      <Users className="w-3 h-3" />
                      Shared with {rec.accessGrantCount} customer
                      {(rec.accessGrantCount ?? 0) > 1 ? 's' : ''}
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <button
                onClick={() => setFilters((p) => ({ ...p, page: (p.page || 1) - 1 }))}
                disabled={pagination.page <= 1}
                className="claude-button claude-button-secondary text-sm p-2 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <button
                onClick={() => setFilters((p) => ({ ...p, page: (p.page || 1) + 1 }))}
                disabled={pagination.page >= pagination.totalPages}
                className="claude-button claude-button-secondary text-sm p-2 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}

      {/* Recording Detail Modal */}
      {selectedId && (
        <RecordingDetail recordingId={selectedId} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
